import { formatDurationSeconds, prettyDateTime } from "@app/date";
import {
  fetchLlmKeyRequestLogs,
  fetchLlmKeyRequests,
  formatCurrency,
  selectEnvironmentById,
  selectLlmKeyById,
  selectLlmKeyRequestById,
} from "@app/deploy";
import { downloadFile } from "@app/file-utils";
import { useLoader, useQuery, useSelector } from "@app/react";
import {
  environmentLlmKeysUrl,
  llmKeyRequestsUrl,
  notFoundUrl,
} from "@app/routes";
import { schema } from "@app/schema";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppSidebarLayout } from "../layouts/app-sidebar-layout";
import {
  Box,
  Breadcrumbs,
  DetailHeader,
  DetailInfoGrid,
  DetailInfoItem,
  DetailTitleBar,
  DropdownMenu,
  Group,
  Pill,
  PreText,
  Switch,
} from "../shared";
import { JsonView } from "../shared/json-view";

/**
 * Walk a JSON object and parse any string values that are valid JSON.
 *
 * @param value - The value to be parsed.
 * @returns The parsed value.
 */
function parseEmbeddedJsonStrings(value: unknown): unknown {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"))
    ) {
      try {
        return parseEmbeddedJsonStrings(JSON.parse(trimmed));
      } catch {
        return value;
      }
    }
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(parseEmbeddedJsonStrings);
  }
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [
        k,
        parseEmbeddedJsonStrings(v),
      ]),
    );
  }
  return value;
}

const LlmLogMenu = ({
  viewRaw,
  onViewRawChange,
  rawContent,
  parsedContent,
  requestId,
}: {
  viewRaw: boolean;
  onViewRawChange: (value: boolean) => void;
  rawContent: string;
  parsedContent: string;
  requestId: string;
}) => {
  return (
    <DropdownMenu
      label="Options"
      items={[
        {
          id: "toggle-view",
          label: (
            <div className="flex items-center justify-between gap-4 w-full">
              <span>View Raw</span>
              <span className="pointer-events-none">
                <Switch checked={viewRaw} onChange={() => {}} />
              </span>
            </div>
          ),
          onClick: () => onViewRawChange(!viewRaw),
        },
        {
          id: "download-parsed",
          label: "Download parsed JSON",
          hasSeparator: true,
          onClick: (close) => {
            downloadFile(
              parsedContent,
              `request-${requestId}.json`,
              "application/json",
            );
            close();
          },
        },
        {
          id: "download-raw",
          label: "Download raw JSON",
          onClick: (close) => {
            downloadFile(
              rawContent,
              `request-${requestId}.raw.json`,
              "application/json",
            );
            close();
          },
        },
      ]}
    />
  );
};

const LlmLogView = ({
  llmKeyId,
  requestId,
}: { llmKeyId: string; requestId: string }) => {
  const logsAction = useMemo(
    () => fetchLlmKeyRequestLogs({ id: llmKeyId, requestId }),
    [llmKeyId, requestId],
  );
  useQuery(logsAction);
  const logsLoader = useLoader(logsAction);

  const logDataStr: string =
    useSelector((s) =>
      schema.cache.selectById(s, { id: logsAction.payload.key }),
    ) ?? "";

  const [viewRaw, setViewRaw] = useState(false);

  const logData = useMemo(() => {
    if (!logDataStr) return null;
    try {
      return JSON.parse(logDataStr);
    } catch {
      return null;
    }
  }, [logDataStr]);

  const parsedLogData = useMemo(
    () => (logData !== null ? parseEmbeddedJsonStrings(logData) : null),
    [logData],
  );

  const rawLogContent = useMemo(
    () => (logData !== null ? JSON.stringify(logData, null, 2) : null),
    [logData],
  );

  const parsedLogContent = useMemo(
    () =>
      parsedLogData !== null ? JSON.stringify(parsedLogData, null, 2) : null,
    [parsedLogData],
  );

  const logsLoading = logsLoader.isLoading || logsLoader.isInitialLoading;

  return (
    <Box>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Log Payload</h3>
        {logData ? (
          <LlmLogMenu
            viewRaw={viewRaw}
            onViewRawChange={setViewRaw}
            rawContent={rawLogContent ?? ""}
            parsedContent={parsedLogContent ?? ""}
            requestId={requestId}
          />
        ) : null}
      </div>
      {logsLoading ? (
        <p className="text-gray-500">Loading logs...</p>
      ) : logsLoader.isError ? (
        <p className="text-gray-500">No logs are available for this request.</p>
      ) : parsedLogData ? (
        viewRaw ? (
          <PreText
            text={rawLogContent ?? ""}
            allowCopy={true}
            className="pr-4 whitespace-pre-wrap break-all overflow-auto"
          />
        ) : (
          <JsonView src={parsedLogData as object} />
        )
      ) : null}
    </Box>
  );
};

export const LlmKeyRequestDetailPage = () => {
  const { id: llmKeyId = "", requestId = "" } = useParams();

  // Fetch the specific request with 7-day date range
  const action = useMemo(() => {
    const now = DateTime.utc();
    const sevenDaysAgo = now.minus({ days: 7 });
    return fetchLlmKeyRequests({
      id: llmKeyId,
      requestId,
      startTime: sevenDaysAgo.toISO() ?? "",
      endTime: now.toISO() ?? "",
      perPage: 1,
    });
  }, [llmKeyId, requestId]);

  useQuery(action);
  const loader = useLoader(action);

  const llmKey = useSelector((s) => selectLlmKeyById(s, { id: llmKeyId }));
  const environment = useSelector((s) =>
    selectEnvironmentById(s, { id: llmKey.environmentId }),
  );
  const request = useSelector((s) =>
    selectLlmKeyRequestById(s, { id: requestId }),
  );

  const isLoading = loader.isLoading || loader.isInitialLoading;
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !request?.id) {
      navigate(notFoundUrl());
    }
  }, [isLoading, request?.id, navigate]);

  return (
    <AppSidebarLayout>
      <Breadcrumbs
        crumbs={[
          {
            name: environment.handle,
            to: environmentLlmKeysUrl(environment.id),
          },
          {
            name: llmKey.handle || "Unknown",
            to: llmKeyRequestsUrl(llmKey.id),
          },
          { name: request.id || "Unknown", to: null },
        ]}
      />
      <Group>
        <DetailHeader>
          <DetailTitleBar
            title="Request Details"
            isLoading={isLoading}
            icon={
              <img
                src="/resource-types/logo-llm_key.png"
                className="w-[32px] h-[32px] mr-3"
                aria-label="LLM Request"
              />
            }
          />

          <DetailInfoGrid columns={3}>
            <DetailInfoItem title="Request ID" isLoading={isLoading}>
              <span className="font-mono">{request.id}</span>
            </DetailInfoItem>
            <DetailInfoItem title="Start Time" isLoading={isLoading}>
              {prettyDateTime(request.startTime)}
            </DetailInfoItem>
            <DetailInfoItem title="Input Tokens" isLoading={isLoading}>
              {request.inputTokens.toLocaleString()}
            </DetailInfoItem>

            <DetailInfoItem title="Status" isLoading={isLoading}>
              <Pill
                variant={request.status === "success" ? "success" : "error"}
                className="capitalize"
              >
                {request.status}
              </Pill>
            </DetailInfoItem>
            <DetailInfoItem title="End Time" isLoading={isLoading}>
              {prettyDateTime(request.endTime)}
            </DetailInfoItem>
            <DetailInfoItem title="Output Tokens" isLoading={isLoading}>
              {request.outputTokens.toLocaleString()}
            </DetailInfoItem>

            <DetailInfoItem title="Model" isLoading={isLoading}>
              {request.model}
            </DetailInfoItem>
            <DetailInfoItem title="Duration" isLoading={isLoading}>
              {formatDurationSeconds(request.durationSeconds)}
            </DetailInfoItem>
            <DetailInfoItem title="Cost" isLoading={isLoading}>
              {formatCurrency(request.spend, 4)}
            </DetailInfoItem>
          </DetailInfoGrid>
        </DetailHeader>

        <LlmLogView llmKeyId={llmKeyId} requestId={requestId} />
      </Group>
    </AppSidebarLayout>
  );
};
